import { useState } from 'react';
import { Box, Typography, Stack, Sheet, Table } from '@mui/joy';

/**
 * Экран «Анализ» для админа: точка отсчёта, среднемесячный доход,
 * график оплат по месяцам (четыре вида по клику), таблица по месяцам.
 *
 * Чисто презентационный: получает ответ /subscriptions/admin/payments-analytics/
 * и ничего не грузит сам. График — свой SVG, без библиотек.
 * Палитра и линии — как в диаграммах Notion: мягкие цвета, тонкие линии,
 * светлая сетка, без чёрного.
 */

const MONTHS_NOM = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн',
    'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

// Цвета Notion. Порядок = порядок стопки снизу вверх: годовой — основа.
const PERIODS = [
    { key: '12', label: '12 мес', color: '#337EA9' },   // blue
    { key: '6', label: '6 мес', color: '#448361' },     // green
    { key: '3', label: '3 мес', color: '#D9730D' },     // orange
    { key: '1', label: '1 мес', color: '#9065B0' },     // purple
];

const CHART_TYPES = [
    { key: 'bars', label: 'Столбцы' },
    { key: 'line', label: 'Линия' },
    { key: 'cumulative', label: 'Накопительно' },
    { key: 'count', label: 'Оплаты' },
];
const CHART_TYPE_KEY = 'apex_admin_chart_type';

const INK = '#37352F';
const MUTED = '#787774';
const AXIS = '#9B9A97';
const LINE = '#E9E7E2';
const GRID = '#EDECE9';
const PILL_BG = '#F1F1EF';
const BLUE = '#337EA9';
const GREEN = '#448361';
const ORANGE = '#D9730D';

export const fmtRub = (n) => `${Math.round(n || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ₽`;
const fmtShort = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(1).replace('.0', '')} млн` : n >= 1000 ? `${Math.round(n / 1000)}к` : String(Math.round(n)));

const monthTitle = (key) => {
    const [y, m] = key.split('-').map(Number);
    return `${MONTHS_NOM[m - 1]} ${y}`;
};
const monthShort = (key) => {
    const [y, m] = key.split('-').map(Number);
    return m === 1 ? `${MONTHS_SHORT[0]} ${y}` : MONTHS_SHORT[m - 1];
};
const dateTitle = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    return `${d} ${MONTHS_GEN[m - 1]} ${y}`;
};

const readChartType = () => {
    try {
        const v = localStorage.getItem(CHART_TYPE_KEY);
        return CHART_TYPES.some(t => t.key === v) ? v : 'bars';
    } catch { return 'bars'; }
};
const saveChartType = (v) => { try { localStorage.setItem(CHART_TYPE_KEY, v); } catch { /* приватный режим */ } };

function Label({ children }) {
    return (
        <Typography level="body-xs" sx={{ color: MUTED, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600 }}>
            {children}
        </Typography>
    );
}

function Tile({ label, value, hint, accent }) {
    return (
        <Sheet variant="outlined" sx={{ p: 1.75, borderRadius: 10, borderColor: LINE, bgcolor: '#FFFFFF', minWidth: 0 }}>
            <Label>{label}</Label>
            <Typography level="h3" sx={{ mt: 0.5, fontWeight: 600, letterSpacing: '-0.01em', color: accent || INK, fontSize: '1.35rem', lineHeight: 1.2 }}>
                {value}
            </Typography>
            {hint && <Typography level="body-xs" sx={{ color: MUTED, mt: 0.5 }}>{hint}</Typography>}
        </Sheet>
    );
}

/**
 * Годовой прогноз «если темп сохранится». Основа — среднее по полным
 * месяцам (без первого и текущего); пока полных месяцев нет, берём среднее
 * с точки отсчёта. Второе число — альтернативная оценка, чтобы был виден разброс.
 */
function YearlyForecast({ summary }) {
    const hasFull = (summary.full_months || 0) > 0 && (summary.avg_full_months || 0) > 0;
    const base = hasFull ? summary.avg_full_months : summary.avg_since_start;
    const alt = summary.avg_since_start || 0;
    const forecast = (base || 0) * 12;
    return (
        <Sheet data-testid="yearly-forecast" variant="outlined" sx={{
            p: 2, borderRadius: 10, borderColor: LINE, bgcolor: '#F7FAFC',
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, justifyContent: 'space-between',
        }}>
            <Box>
                <Label>Прогноз на год</Label>
                <Typography level="h2" sx={{ fontWeight: 600, letterSpacing: '-0.02em', color: BLUE, mt: 0.25, lineHeight: 1.15 }}>
                    {fmtRub(forecast)}
                </Typography>
                <Typography level="body-xs" sx={{ color: MUTED, mt: 0.5 }}>
                    {hasFull
                        ? `если темп сохранится: ${fmtRub(base)} в месяц × 12, по ${summary.full_months} полным месяцам`
                        : `если темп сохранится: ${fmtRub(base)} в месяц × 12, по среднему с точки отсчёта`}
                </Typography>
            </Box>
            {hasFull && (
                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                    <Typography level="body-xs" sx={{ color: MUTED }}>по среднему с точки отсчёта</Typography>
                    <Typography level="title-md" sx={{ color: INK, fontWeight: 600 }}>{fmtRub(alt * 12)}</Typography>
                    <Typography level="body-xs" sx={{ color: MUTED }}>{fmtRub(alt)} × 12</Typography>
                </Box>
            )}
        </Sheet>
    );
}

function Segmented({ value, onChange }) {
    return (
        <Stack direction="row" sx={{ bgcolor: PILL_BG, borderRadius: 8, p: '3px', gap: '2px' }}>
            {CHART_TYPES.map(t => {
                const active = t.key === value;
                return (
                    <Box key={t.key} component="button" type="button" onClick={() => onChange(t.key)} sx={{
                        border: active ? `1px solid ${LINE}` : '1px solid transparent',
                        bgcolor: active ? '#FFFFFF' : 'transparent',
                        color: active ? INK : MUTED,
                        borderRadius: 6, px: 1.25, py: 0.5, fontSize: '0.78rem', fontWeight: 500,
                        fontFamily: 'inherit', cursor: 'pointer', lineHeight: 1.4,
                        boxShadow: active ? '0 1px 2px rgba(0,0,0,0.04)' : 'none',
                        '&:hover': { color: INK },
                    }}>
                        {t.label}
                    </Box>
                );
            })}
        </Stack>
    );
}

function Tooltip({ month, type, cumulative }) {
    return (
        <Sheet variant="outlined" sx={{
            position: 'absolute', top: 0, right: 0, px: 1.25, py: 1, borderRadius: 8, borderColor: LINE,
            bgcolor: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', minWidth: 180, zIndex: 1,
        }}>
            <Typography level="body-sm" sx={{ fontWeight: 600, color: INK }}>{monthTitle(month.month)}</Typography>
            <Typography level="body-xs" sx={{ color: MUTED, mb: 0.5 }}>
                {type === 'cumulative'
                    ? `${fmtRub(cumulative)} всего · +${fmtRub(month.total)}`
                    : `${fmtRub(month.total)} · ${month.count} опл.`}
            </Typography>
            {PERIODS.filter(p => month.count_by_period[p.key] > 0).map(p => (
                <Stack key={p.key} direction="row" justifyContent="space-between" spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box sx={{ width: 8, height: 8, borderRadius: 2, bgcolor: p.color }} />
                        <Typography level="body-xs" sx={{ color: INK }}>{p.label} · {month.count_by_period[p.key]}</Typography>
                    </Stack>
                    <Typography level="body-xs" sx={{ color: INK }}>{fmtRub(month.by_period[p.key])}</Typography>
                </Stack>
            ))}
        </Sheet>
    );
}

function Chart({ months, type }) {
    const [hover, setHover] = useState(null);
    const W = 760, H = 250, padL = 52, padR = 16, padT = 16, padB = 30;
    const innerW = W - padL - padR, innerH = H - padT - padB;
    const n = months.length;
    const slot = innerW / n;
    const cx = (i) => padL + slot * i + slot / 2;

    let cumulative = 0;
    const cumulativeValues = months.map(m => (cumulative += m.total));
    const values = type === 'count' ? months.map(m => m.count)
        : type === 'cumulative' ? cumulativeValues
            : months.map(m => m.total);

    const max = Math.max(1, ...values);
    let ceil;
    if (type === 'count') {
        ceil = Math.max(4, Math.ceil(max / 4) * 4);
    } else {
        const step = Math.pow(10, Math.floor(Math.log10(max)));
        ceil = Math.ceil(max / step) * step;
    }
    const y = (v) => padT + innerH - (v / ceil) * innerH;
    const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => ceil * f);
    const hovered = hover != null ? months[hover] : null;
    const barW = Math.min(44, slot * 0.58);

    const linePath = (vals) => vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${cx(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
    const areaPath = (vals) => `${linePath(vals)} L${cx(n - 1).toFixed(1)},${y(0)} L${cx(0).toFixed(1)},${y(0)} Z`;

    const lineColor = type === 'cumulative' ? GREEN : BLUE;

    return (
        <Box sx={{ position: 'relative' }}>
            {hovered && <Tooltip month={hovered} type={type} cumulative={cumulativeValues[hover]} />}
            <svg data-testid="payments-chart" viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', overflow: 'visible' }}
                onMouseLeave={() => setHover(null)}>
                {ticks.map(t => (
                    <g key={t}>
                        <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} stroke={GRID} strokeWidth={1} />
                        <text x={padL - 10} y={y(t) + 4} fontSize={11} fill={AXIS} textAnchor="end" fontFamily="Inter, sans-serif">
                            {type === 'count' ? Math.round(t) : fmtShort(t)}
                        </text>
                    </g>
                ))}

                {type === 'bars' && months.map((m, i) => {
                    let acc = 0;
                    const dim = hover != null && hover !== i;
                    const x = cx(i) - barW / 2;
                    return (
                        <g key={m.month}>
                            {PERIODS.map(p => {
                                const v = m.by_period[p.key] || 0;
                                if (!v) return null;
                                const top = y(acc + v), bottom = y(acc);
                                acc += v;
                                return (
                                    <rect key={p.key} data-testid="bar-segment" data-period={p.key} data-month={m.month}
                                        x={x} y={top} width={barW} height={Math.max(1, bottom - top)}
                                        fill={p.color} opacity={dim ? 0.35 : 0.9} />
                                );
                            })}
                        </g>
                    );
                })}

                {type === 'count' && months.map((m, i) => {
                    if (!m.count) return null;
                    const dim = hover != null && hover !== i;
                    return (
                        <rect key={m.month} data-testid="count-bar" data-month={m.month}
                            x={cx(i) - barW / 2} y={y(m.count)} width={barW} height={Math.max(1, y(0) - y(m.count))}
                            fill={ORANGE} opacity={dim ? 0.35 : 0.85} rx={3} />
                    );
                })}

                {(type === 'line' || type === 'cumulative') && (
                    <g>
                        <path d={areaPath(values)} fill={lineColor} opacity={0.1} />
                        <path data-testid={type === 'line' ? 'line-path' : 'cumulative-path'}
                            d={linePath(values)} fill="none" stroke={lineColor} strokeWidth={2}
                            strokeLinejoin="round" strokeLinecap="round" />
                        {values.map((v, i) => (
                            <circle key={months[i].month} cx={cx(i)} cy={y(v)} r={hover === i ? 5 : 3.5}
                                fill="#FFFFFF" stroke={lineColor} strokeWidth={2} />
                        ))}
                    </g>
                )}

                {months.map((m, i) => (
                    <g key={m.month} onMouseEnter={() => setHover(i)} style={{ cursor: 'default' }}>
                        <rect x={padL + slot * i} y={padT} width={slot} height={innerH} fill="transparent" />
                        {hover === i && (type === 'line' || type === 'cumulative') && (
                            <line x1={cx(i)} x2={cx(i)} y1={padT} y2={y(0)} stroke={LINE} strokeWidth={1} strokeDasharray="3 3" />
                        )}
                        <text x={cx(i)} y={H - 10} fontSize={11} fill={hover === i ? INK : AXIS} textAnchor="middle" fontFamily="Inter, sans-serif">
                            {monthShort(m.month)}
                        </text>
                    </g>
                ))}
            </svg>

            {type === 'bars' && (
                <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap' }}>
                    {PERIODS.map(p => (
                        <Stack key={p.key} direction="row" alignItems="center" spacing={0.75}>
                            <Box sx={{ width: 10, height: 10, borderRadius: 2, bgcolor: p.color }} />
                            <Typography level="body-xs" sx={{ color: MUTED }}>{p.label}</Typography>
                        </Stack>
                    ))}
                </Stack>
            )}
            {type !== 'bars' && (
                <Typography level="body-xs" sx={{ color: MUTED, mt: 1 }}>
                    {type === 'line' && 'Выручка за месяц, все сроки вместе'}
                    {type === 'cumulative' && 'Выручка нарастающим итогом с точки отсчёта'}
                    {type === 'count' && 'Число оплат за месяц'}
                </Typography>
            )}
        </Box>
    );
}

const SOURCE_LABEL = { telegram: 'Telegram', instagram: 'Instagram', friends: 'Друзья', search: 'Поиск', other: 'Другое', test: 'Тест' };
const CHANNEL_LABEL = { yookassa: 'ЮKassa', prodamus: 'Prodamus', manual: 'вручную' };
const fmtDay = (iso) => { try { return new Date(iso + 'T12:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }); } catch { return iso; } };

function RecentPayments({ rows }) {
    if (!rows || rows.length === 0) return null;
    const cell = { py: 1, px: 1, borderBottom: `1px solid ${GRID}`, fontSize: 13, whiteSpace: 'nowrap' };
    const head = { ...cell, color: MUTED, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' };
    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 10, borderColor: LINE, bgcolor: '#FFFFFF' }}>
            <Label>Последние покупки</Label>
            <Typography level="body-xs" sx={{ color: MUTED, mb: 1 }}>последние {rows.length} реальных оплат · подарки и тестовые суммы не попадают</Typography>
            <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" data-testid="recent-payments" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                    <thead>
                        <tr>
                            <Box component="th" sx={{ ...head, textAlign: 'left' }}>Дата</Box>
                            <Box component="th" sx={{ ...head, textAlign: 'left' }}>Кто</Box>
                            <Box component="th" sx={{ ...head, textAlign: 'left' }}>Источник</Box>
                            <Box component="th" sx={{ ...head, textAlign: 'left' }}>Тариф</Box>
                            <Box component="th" sx={{ ...head, textAlign: 'right' }}>Сумма</Box>
                            <Box component="th" sx={{ ...head, textAlign: 'left' }}>Канал</Box>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.id} data-testid="recent-row">
                                <Box component="td" sx={{ ...cell, color: MUTED }}>{fmtDay(r.date)}</Box>
                                <Box component="td" sx={{ ...cell, whiteSpace: 'normal', minWidth: 180 }}>
                                    <Box sx={{ fontWeight: 600, color: INK }}>{r.name || '—'}</Box>
                                    <Box sx={{ fontSize: 12, color: MUTED }}>{r.email}</Box>
                                </Box>
                                <Box component="td" sx={cell}>{SOURCE_LABEL[r.source] || (r.source ? r.source : '—')}</Box>
                                <Box component="td" sx={cell}>{r.tariff}</Box>
                                <Box component="td" sx={{ ...cell, textAlign: 'right', fontWeight: 600, color: INK }}>{fmtRub(r.amount)}</Box>
                                <Box component="td" sx={{ ...cell, color: MUTED }}>{CHANNEL_LABEL[r.channel] || r.channel}</Box>
                            </tr>
                        ))}
                    </tbody>
                </Box>
            </Box>
        </Sheet>
    );
}

export default function PaymentsAnalytics({ data }) {
    const { first_payment_date: firstDate, months = [], summary = {}, recent_payments: recent = [] } = data || {};
    const [chartType, setChartType] = useState(readChartType);
    const changeType = (v) => { setChartType(v); saveChartType(v); };

    if (!firstDate || months.length === 0) {
        return (
            <Sheet variant="outlined" sx={{ p: 3, borderRadius: 10, borderColor: LINE, bgcolor: '#FFFFFF', textAlign: 'center' }}>
                <Typography level="title-md" sx={{ color: INK }}>Оплат ещё не было</Typography>
                <Typography level="body-sm" sx={{ color: MUTED, mt: 0.5 }}>
                    График появится после первой реальной оплаты. Подарки и тестовые суммы сюда не попадают.
                </Typography>
            </Sheet>
        );
    }

    const cur = summary.current_month || {};
    const byPeriod = summary.revenue_by_period || {};
    const total = summary.total_revenue || 0;

    return (
        <Stack spacing={3}>
            <Box>
                <Label>Точка отсчёта</Label>
                <Typography level="h2" sx={{ fontWeight: 600, letterSpacing: '-0.02em', color: INK, mt: 0.25 }}>
                    {dateTitle(firstDate)}
                </Typography>
                <Typography level="body-sm" sx={{ color: MUTED }}>
                    первая реальная оплата · {summary.months_since_start} мес назад · {summary.total_payments} оплат на {fmtRub(total)}
                </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.25 }}>
                <Tile label="Среднее по полным месяцам" value={fmtRub(summary.avg_full_months)}
                    hint={`${summary.full_months} полных мес., без первого и текущего`} accent={BLUE} />
                <Tile label="Среднее с точки отсчёта" value={fmtRub(summary.avg_since_start)}
                    hint={`${fmtRub(total)} ÷ ${summary.months_since_start} мес`} accent={BLUE} />
                <Tile label={`Текущий месяц · ${monthTitle(cur.month || '2026-01').split(' ')[0]}`} value={fmtRub(cur.total)}
                    hint={`${cur.count || 0} опл. · прогноз ${fmtRub(cur.projected)}`} accent={GREEN} />
                <Tile label="Всего" value={fmtRub(total)} hint={`${summary.total_payments} оплат · средний чек ${fmtRub(summary.avg_check)}`} />
            </Box>

            <YearlyForecast summary={summary} />

            <RecentPayments rows={recent} />

            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 10, borderColor: LINE, bgcolor: '#FFFFFF' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                        <Label>Оплаты по месяцам</Label>
                        {summary.best_month && (
                            <Typography level="body-xs" sx={{ color: MUTED }}>
                                лучший: {monthTitle(summary.best_month.month)} · {fmtRub(summary.best_month.total)} · за 30 дней {fmtRub(summary.last_30_days)}
                            </Typography>
                        )}
                    </Box>
                    <Segmented value={chartType} onChange={changeType} />
                </Stack>
                <Chart months={months} type={chartType} />
            </Sheet>

            <Box>
                <Label>Доля по срокам</Label>
                <Stack direction="row" sx={{ mt: 1, height: 10, borderRadius: 5, overflow: 'hidden', bgcolor: GRID }}>
                    {PERIODS.map(p => byPeriod[p.key] > 0 && (
                        <Box key={p.key} sx={{ width: `${(byPeriod[p.key] / total) * 100}%`, bgcolor: p.color, opacity: 0.9 }} />
                    ))}
                </Stack>
                <Stack direction="row" spacing={2.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
                    {PERIODS.map(p => (
                        <Typography key={p.key} level="body-xs" sx={{ color: MUTED }}>
                            <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, bgcolor: p.color, mr: 0.75 }} />
                            {p.label} <b style={{ color: INK }}>{total ? Math.round((byPeriod[p.key] / total) * 100) : 0}%</b> · {fmtRub(byPeriod[p.key])}
                        </Typography>
                    ))}
                </Stack>
            </Box>

            <Box>
                <Label>По месяцам</Label>
                <Table size="sm" sx={{
                    mt: 1, '--TableCell-paddingY': '8px', '--TableCell-borderColor': LINE,
                    '& th': { color: MUTED, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', bgcolor: 'transparent' },
                    '& td': { color: INK, fontSize: '0.85rem' },
                }}>
                    <thead>
                        <tr>
                            <th>Месяц</th>
                            <th style={{ textAlign: 'right' }}>Оплат</th>
                            {PERIODS.slice().reverse().map(p => <th key={p.key} style={{ textAlign: 'right', color: p.color }}>{p.label}</th>)}
                            <th style={{ textAlign: 'right' }}>Итого</th>
                        </tr>
                    </thead>
                    <tbody>
                        {months.slice().reverse().map(m => (
                            <tr key={m.month} data-testid={`month-row-${m.month}`} style={{ opacity: m.total ? 1 : 0.55 }}>
                                <td>{monthTitle(m.month)}</td>
                                <td style={{ textAlign: 'right' }}>{m.count}</td>
                                {PERIODS.slice().reverse().map(p => (
                                    <td key={p.key} style={{ textAlign: 'right', color: m.by_period[p.key] ? INK : AXIS }}>
                                        {m.by_period[p.key] ? fmtRub(m.by_period[p.key]) : '·'}
                                    </td>
                                ))}
                                <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtRub(m.total)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Box>
        </Stack>
    );
}
